import {createSlice} from '@reduxjs/toolkit'
import SlideData, {UploadStatus} from "../Interfaces/SlideData";
import ThemeData from "../Interfaces/ThemeData";
import MusicData, {MUSIC_TYPE_LOCAL, MUSIC_TYPE_USER} from "../Interfaces/MusicData";
import {v4 as uuidv4} from "uuid";
import VideoDuration from "../Interfaces/VideoDuration";
import SavedProjectData from "../Interfaces/SavedProjectData";
import PreviewData from "../Interfaces/PreviewData";
import * as Sentry from "@sentry/react";
import {sha256} from "js-sha256";
import {OnBoardingStep} from "../Interfaces/OnBoardingStep";
import VkLaunchParams from "../Interfaces/VkLaunchParams";
import VkUserProjectData from "../Interfaces/VkUserProjectData";

export const PREVIEW_DEFAULT_VOLUME = 70;

// Define a type for the slice state
export interface MakerDataState {
    isLoaded: boolean,
    isLoadedAndInitialized: boolean,
    slides: SlideData[],
    slidesNumReady: number, // кол-во слайдов, которые в итоге пойдут в проект (в статусе Ready)
    themes: ThemeData[],
    music: MusicData[],
    currentSlide?: SlideData | null,
    currentTheme: ThemeData,
    currentMusic?: MusicData | null,
    tmpProjectId: string,
    email: string,
    savedProjectPaymentDone: boolean,
    allowCheckPaymentDone: boolean,

    videoDuration: VideoDuration | null,
    isMusicPlaying: boolean,
    previewVolume: number,
    themePopupVolume: number,
    musicVolume: number,

    savedProjectData: SavedProjectData | null,
    previewData: PreviewData | null,
    previewId: string | null,
    previewIdStateHash: string | null, // хэш, для которого был создан preview
    userId: string | null,
    previewCreatedAt?: number | null,
    isPreviewPlaying: boolean,
    scenes: string,
    stateHash: string,
    isPreviewRestored: boolean,
    isAllPhotosUploaded: boolean,
    isAllMusicUploaded: boolean,

    fullVideoPrice: number | null,
    fullVideoMakeTime: number | null, // кол-во минут, через сколько будет готово полное видео
    fullVideoMakeTimeStr: string | null, // кол-во минут, через сколько будет готово полное видео в виде строки: 1 час 20 минут

    promoCodeResponse: any,
    promoCode: string,
    promoCodeDiscount: number,

    currentTab: string,
    isAddSlidePopupOpen: boolean,
    isRestoredFromPayedProject: boolean,

    info: string,
    infoC: boolean,

    cardSlug: string | null, // slug открытки из которой нажали "Сделать своё", поле не сохраняется в localStorage
    cardSlugTmp: string | null, // slug открытки из которой нажали "Сделать своё"

    onBoardingStep: string,
    onBoardingStepsShown: number[], // шаги, которые уже были показаны пользователю
    onBoardingShow: boolean,
    onBoardingResetTime: number | undefined,
    onBoardingShowReorderPopup: boolean | undefined,

    vkLaunchParams?: VkLaunchParams | undefined | null,
    vkClientVersion?: string | null | undefined,
    vkUserProjects: VkUserProjectData[],

    ymClientId: string,

    musicUploadPidOnlyOneFile: string,

    cardsBeforePay: string[] // записываем slug открыток, у которых нажали "сделать своё" перед тем как оплатили видео
}

const initialTmpProjectId = uuidv4();
// Define the initial state using that type
const initialState: MakerDataState = {
    isLoaded: false,
    isLoadedAndInitialized: false,
    slides: [
        {pid: 'add-slide-item', id: 'add-slide-item', status: UploadStatus.ready}
    ],
    slidesNumReady: 0,
    themes: [],
    music: [],
    currentSlide: null,
    currentTheme: {} as ThemeData,
    currentMusic: null,
    tmpProjectId: initialTmpProjectId,
    email: '',
    savedProjectPaymentDone: false,
    allowCheckPaymentDone: false,

    videoDuration: null,
    isMusicPlaying: false,
    previewVolume: PREVIEW_DEFAULT_VOLUME,
    themePopupVolume: 1,
    musicVolume: 1,

    savedProjectData: null,
    previewData: null,
    previewId: null,
    previewIdStateHash: null,
    userId: initialTmpProjectId.substring(0, 8),
    previewCreatedAt: null,
    isPreviewPlaying: false,
    scenes: '',
    stateHash: '',
    isPreviewRestored: false,
    isAllPhotosUploaded: true,
    isAllMusicUploaded: true,

    fullVideoPrice: null,
    fullVideoMakeTime: null,
    fullVideoMakeTimeStr: null,

    promoCodeResponse: null,
    promoCode: '',
    promoCodeDiscount: 0,

    currentTab: 'themes',
    isAddSlidePopupOpen: false,
    isRestoredFromPayedProject: false,

    info: '',
    infoC: false,
    cardSlug: null,
    cardSlugTmp: null,

    onBoardingStep: OnBoardingStep.themes,
    onBoardingStepsShown: [],
    onBoardingShow: true,
    onBoardingResetTime: undefined,
    onBoardingShowReorderPopup: undefined,

    vkLaunchParams: null,
    vkClientVersion: null,
    vkUserProjects: [],

    ymClientId: '',

    // Определяем pid загружаемой мелодии на всё приложение, чтобы можно было загружать только одну свою мелодию, а не несколько
    musicUploadPidOnlyOneFile: uuidv4(),

    cardsBeforePay: []
}

export const makerDataSlice = createSlice({
    name: 'makerData',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setLoaded: (state) => {
            state.isLoaded = true;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        addSlide: (state, action) => {
            var i = state.slides.findIndex(s => s.pid === action.payload.pid);
            if (i === -1) {
                if (action.payload.status === UploadStatus.uploading) {
                    // добавляем несуществующий слайд только если у него статус uploading.
                    // если слайда не существует, а у добавляемого другой статус - значит
                    // его успели удалить через кнопку "удалить все слайды"
                    state.slides = [...state.slides, action.payload];
                }
            } else {
                state.slides[i] = action.payload;
            }

            // move addSlideItem to them end
            var addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
            state.slides.push(state.slides.splice(addSlideItemIndex, 1)[0]);

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        removeSlide: (state, action) => {
            if (state.currentSlide !== null) {
                const currentSlideIndex = Number(state.slides.findIndex((value) => {
                    return state.currentSlide?.pid == value.pid
                }));

                state.currentSlide = currentSlideIndex == Number(state.slides.length) - 2
                    ? state.slides[0]
                    : state.slides[currentSlideIndex + 1];
            }
            if (state.slides.length === 2) {
                state.currentSlide = null;
            }

            state.slides = state.slides.filter(item => item.pid !== action.payload.pid)

            // move addSlideItem to them end
            var addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
            state.slides.push(state.slides.splice(addSlideItemIndex, 1)[0]);

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        removeAllSlides: (state) => {
            const addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
            const addSlideItem = state.slides[addSlideItemIndex];
            state.slides = [];
            state.slides.push(addSlideItem);

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setSlides: (state, action) => {
            state.slides = action.payload;
            var addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
            if (addSlideItemIndex === -1) {
                state.slides.push({pid: 'add-slide-item', id: 'add-slide-item', status: UploadStatus.ready});
            } else {
                state.slides.push(state.slides.splice(addSlideItemIndex, 1)[0]);
            }

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setSlideUploadProgress: (state, action) => {
            var i = state.slides.findIndex(s => s.pid === action.payload.pid);
            if (i !== -1) {
                state.slides[i].uploadProgress = action.payload.uploadProgress;
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setSlideUploadStatus: (state, action) => {
            var i = state.slides.findIndex(s => s.pid === action.payload.pid);
            if (i !== -1 && typeof state.slides[i] !== 'undefined') {
                state.slides[i].status = action.payload.status;
            }

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setThemes: (state, action) => {
            state.themes = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setCurrentSlide: (state, action) => {
            state.currentSlide = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setCurrentTheme: (state, action) => {
            state.currentTheme = action.payload;

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setThemeActive: (state, action) => {
            state.themes.map((theme) => {
                theme.isActive = false;
            })
            var i = state.themes.findIndex(s => s.themeId === action.payload.themeId);
            if (i > -1) {
                state.themes[i].isActive = true;
            }

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setMusic: (state, action) => {
            state.music = action.payload;
        },
        setCurrentMusic: (state, action) => {
            state.currentMusic = action.payload;
            state.music.map(item => {
                item.isActive = item.id == state.currentMusic?.id;
            });

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setIsMusicPlaying: (state, action) => {
            state.isMusicPlaying = action.payload;
        },
        addMusic: (state, action) => {
            var i = state.music.findIndex(s => s.id === action.payload.id);
            if (i === -1) {
                state.music = [...state.music, action.payload];
            } else {
                state.music[i] = action.payload;
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setMusicUploadProgress: (state, action) => {
            var i = state.music.findIndex(s => s.id === action.payload.id);
            if (i !== -1) {
                state.music[i].uploadProgress = action.payload.uploadProgress;
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        removeMusic: (state, action) => {
            if (state.currentMusic?.id == action.payload.id) {
                state.isMusicPlaying = false;
                state.currentMusic = null;
            }
            state.music = state.music.filter(item => item.id !== action.payload.id)


            if (state.music.filter(musicItemData => {
                return musicItemData.type == MUSIC_TYPE_USER
            }).length > 0) {
                state.music.filter(musicItemData => {
                    return musicItemData.type == MUSIC_TYPE_USER
                }).map(musicItemData => {
                    if (musicItemData.status == UploadStatus.ready) {
                        const newCurrentMusicIndex = Math.max(Number(state.music.findIndex((value) => {
                            return musicItemData?.id == value.id;
                        })), 0);
                        state.currentMusic = state.music[newCurrentMusicIndex];
                    }
                })
            }

            if (state.currentMusic == null) {
                state.currentMusic = state.music[0];
            }

            state.music.map(item => {
                item.isActive = item.id == state.currentMusic?.id;
            });

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setEmail: (state, action) => {
            state.email = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setStateFromBackend: (state, action) => {

            state.isLoaded = true;

            const backendMakerData = action.payload;
            const thirtyDaysAgo = Math.round(new Date().getTime() / 1000) - 60 * 60 * 24 * 30;

            state.info = backendMakerData?.info;
            if (state.info === '') {
                state.infoC = false;
            }
            state.savedProjectPaymentDone = backendMakerData?.savedProjectPaymentDone;
            state.themes = backendMakerData?.themes;

            /**
             * Если пользователь сперва заходил через одну открытку, а потом зашёл через другую открытку
             * то cardChanged будет true
             * и нужно будет загрузить слайды, тему и мелодии из второй открытки
             * но если он зашёл через первую открытку, а потом ещё раз зашёл через первую открытку - то
             * нужно оставить всё, что есть в localstorage (слайды, мелодии), т.к. он мог что-то загрузить уже
             * это свзяано с тем, что когда пользователь заходит через открытку и идёт на форму оплаты, а потом нажимает
             * на кнопку "Назад" - то всё что он загрузил - сбрасывается и показывается начальная открытка
             */
            const doRestoreProject =
                backendMakerData?.uid != null &&
                (
                    backendMakerData?.uid != 'card' || (
                        backendMakerData?.uid == 'card' &&
                        typeof backendMakerData?.cardSlug !== 'undefined' &&
                        state.cardSlug != backendMakerData?.cardSlug
                    ) ||
                    typeof backendMakerData?.customTextName !== 'undefined'
                );
            if (backendMakerData?.uid == 'card' &&
                typeof backendMakerData?.cardSlug !== 'undefined' &&
                state.cardSlug != backendMakerData?.cardSlug) {

                state.cardsBeforePay.push(backendMakerData?.cardSlug);
                // make unique
                state.cardsBeforePay = state.cardsBeforePay.filter((value, index, array) => array.indexOf(value) === index);
            }

            // достаём только слайды, которые загрузил пользователь, чтобы подставить в другую открытку
            let userSlides: SlideData[] = state?.slides.filter((slide: object) => {
                if (typeof slide === 'undefined') {
                    return false;
                }
                // @ts-ignore
                return typeof slide?.createdByUser !== 'undefined' && slide?.createdByUser === true;
            });
            // Если загрузили фото и затем зашли ещё раз в ту же самую открытку - то не подставляем отдельно загруженные фото
            // т.к. мы восстановим полностью весь список слайдов, включая и загруженные фото
            if (
                backendMakerData?.uid == 'card' &&
                typeof backendMakerData?.cardSlug !== 'undefined' &&
                state.cardSlug == backendMakerData?.cardSlug &&
                typeof backendMakerData?.customTextName === 'undefined'
            ) {
                userSlides = [];
            }

            if (typeof backendMakerData?.cardSlug !== 'undefined' && backendMakerData?.cardSlug != null) {
                state.cardSlug = backendMakerData?.cardSlug;
                state.cardSlugTmp = backendMakerData?.cardSlug;
            }

            if (doRestoreProject) {
                state.isRestoredFromPayedProject = true;
            }
            if (doRestoreProject || state.currentTheme.themeId == null) {
                let currentTheme = backendMakerData?.themes.filter((theme: ThemeData) => {
                    return theme.isActive;
                })[0];
                if (!currentTheme) {
                    currentTheme = backendMakerData?.themes[0];
                }
                state.currentTheme = currentTheme;
            } else if (typeof state.currentTheme.themeId !== 'undefined' && state.currentTheme.themeId !== null) {
                // если есть активная тема в localStorage - устанавливаем isActive в соответствующей теме в themes
                state.themes.map((theme) => {
                    if (theme.themeId == state.currentTheme.themeId) {
                        state.currentTheme = theme;
                        theme.isActive = true;
                    } else {
                        theme.isActive = false;
                    }
                })
            }

            // если uid проект - удаляем пользовательские мелодии и восстанавливаем из данных проекта только пользовательские
            if (doRestoreProject) {
                // удаляем пользовательские мелодии
                state.music = state.music.filter(item => item.type === MUSIC_TYPE_LOCAL)
                // добавляем пользовательские мелодии из проекта
                backendMakerData?.music.forEach((item: MusicData) => {
                    if (item.type === MUSIC_TYPE_USER) {
                        state.music.push(item);
                        state.musicUploadPidOnlyOneFile = item.id;
                    }
                });
            }
            // при любом раскладе добавляем локальные мелодии
            backendMakerData?.music.forEach((item: MusicData) => {
                if (item.type === MUSIC_TYPE_LOCAL) {
                    state.music.push(item);
                }
            });
            // фильтруем, если мелодия загружена больше 30 дней назад - удаляем
            state.music = state.music.filter((musicItem: MusicData) => {
                if (
                    typeof musicItem.createdAt !== 'undefined' && musicItem.createdAt !== null &&
                    musicItem.createdAt < thirtyDaysAgo
                ) {
                    return false;
                }
                return true;
            });

            if (doRestoreProject || state.currentMusic == null) {
                let currentMusic = backendMakerData?.music.filter((music: MusicData) => {
                    return music.isActive;
                })[0];
                if (!currentMusic) {
                    currentMusic = backendMakerData?.music[0];
                }
                state.currentMusic = currentMusic;
            } else if (state.currentMusic !== null) {
                // если есть активная мелодия в localStorage - устанавливаем isActive в соответствующей мелодии
                state.music.map((music: MusicData) => {
                    if (music.id == state.currentMusic?.id) {
                        state.currentMusic = music;
                        music.isActive = true;
                    } else {
                        music.isActive = false;
                    }
                })
            }


            /**
             * Если пользователь загрузил свою мелодию
             * а потом через 2 месяца заходит опять в редактор,
             * то у него в LocalStorage сохранена state.currentMusic = загруженная мелодия
             * но в цикле выше мы фильтруем все мелодии и если она была загружена больше месяца назад - то удаляем
             * её из state.music массива, а в state.currentMusic она остаётся и в итоге не делается preview
             * чтобы починить это мы проходит ещё раз по state.music и смотрим, есть ли там isActive мелодия
             * если нет - то выставляем state.currentMusic=state.music[0]
             */
            let hasActiveMusic = false;
            state.music.map((musicItem: MusicData) => {
                if (musicItem.isActive) {
                    hasActiveMusic = true;
                }
            });
            if (!hasActiveMusic) {
                state.currentMusic = state.music[0];
                state.music[0].isActive = true;
            }



            // фильтруем слайды с сервера, те, что старше 30 дней - удаляем
            backendMakerData.slides = backendMakerData.slides.filter((backendSlide: object) => {
                if (typeof backendSlide === 'undefined') {
                    return false;
                }
                if (
                    // @ts-ignore
                    typeof backendSlide?.createdAt !== 'undefined' && backendSlide?.createdAt !== null &&
                    // @ts-ignore
                    backendSlide?.createdAt < thirtyDaysAgo
                ) {
                    return false;
                }
                return true;
            });

            if (doRestoreProject) {
                state.slides = [];
                backendMakerData?.slides.map((backendSlide: object) => {
                    // @ts-ignore
                    if (backendSlide?.type == 'text') {
                        state.slides.push({
                            pid: uuidv4(),
                            previewId: uuidv4(),
                            // @ts-ignore
                            pathInfoFilename: backendSlide?.pathInfoFilename,
                            // @ts-ignore
                            src: backendSlide?.src,
                            // @ts-ignore
                            medium_url: backendSlide?.medium_url,
                            // @ts-ignore
                            thumbnail_url: backendSlide?.thumbnail_url,
                            // @ts-ignore
                            type: backendSlide?.type,
                            status: UploadStatus.uploadSuccess,
                            // @ts-ignore
                            text: backendSlide?.text.replace(/\\"/g, '"') // делаем unescape \" => ", чтобы не появлялись слэши при повторном сохранении проекта
                        } as SlideData)
                    }
                    // @ts-ignore
                    if (backendSlide?.type == 'photo') {
                        state.slides.push({
                            pid: uuidv4(),
                            previewId: uuidv4(),
                            // @ts-ignore
                            pathInfoFilename: backendSlide?.pathInfoFilename,
                            // @ts-ignore
                            src: backendSlide?.src,
                            // @ts-ignore
                            medium_url: backendSlide?.medium_url,
                            // @ts-ignore
                            thumbnail_url: backendSlide?.thumbnail_url,
                            width: 1,
                            height: 1,
                            // @ts-ignore
                            type: backendSlide?.type,
                            status: UploadStatus.uploadSuccess
                        } as SlideData)
                    }
                })
                userSlides.map((slideData: SlideData) => {
                    state.slides.push(slideData);
                })

                var addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
                if (addSlideItemIndex === -1) {
                    state.slides.push({pid: 'add-slide-item', id: 'add-slide-item', status: UploadStatus.ready});
                } else {
                    state.slides.push(state.slides.splice(addSlideItemIndex, 1)[0]);
                }
            }

            state.vkUserProjects = backendMakerData.vkUserProjects;

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));

            state.isLoadedAndInitialized = true;
        },
        setProjectSaveResultFromBackend: (state, action) => {
            const data = action.payload;
            state.savedProjectData = {
                id: data?.id,
                uid: data?.uid,
                vp: data?.vp,
                email: data?.email,
                price: data?.price,
                result: data.result,
                showUrl: data?.showUrl
            };

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPreviewSaveResultFromBackend: (state, action) => {
            const data = action.payload;
            state.scenes = data?.scenes;
            state.previewCreatedAt = Math.round(new Date().getTime() / 1000);
            state.fullVideoPrice = data?.fullVideoPrice;
            state.fullVideoMakeTime = data?.fullVideoMakeTime;
            state.fullVideoMakeTimeStr = data?.fullVideoMakeTimeStr;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPreviewDataFromBackend: (state, action) => {
            const data = action.payload;
            if (data?.previewId === state.previewId) {
                if (data.chunks.length === 0) {
                    state.previewData = null;
                    Sentry.captureException(
                        new Error('Preview: chunks is empty'),
                        {
                            extra: {data: data}
                        }
                    )
                    return;
                } else {
                    state.previewData = data as PreviewData;
                }
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPreviewId: (state, action) => {
            const value = action.payload;
            if (value === null) {
                state.previewId = null;
                state.previewData = null;
                state.previewIdStateHash = null;
            } else {
                state.previewId = value;
                state.previewIdStateHash = state.stateHash;
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPreviewVolume: (state, action) => {
            state.previewVolume = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setThemePopupVolume: (state, action) => {
            state.themePopupVolume = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setMusicVolume: (state, action) => {
            state.musicVolume = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setIsPreviewPlaying: (state, action) => {
            state.isPreviewPlaying = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPromoCodeFromBackend: (state, action) => {
            const data = action.payload;
            if (data?.result === 'ok' && typeof data?.discount !== 'undefined') {
                state.promoCodeDiscount = parseInt(data?.discount);
                state.promoCode = data?.promoCode;
            } else {
                state.promoCodeDiscount = 0;
            }
            state.promoCodeResponse = data;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setPromoCodeDiscount: (state, action) => {
            state.promoCodeDiscount = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        resetProjectSave: (state, action) => {
            state.savedProjectPaymentDone = false;
            state.savedProjectData = null;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        restorePreviewDataFromBackend: (state, action) => {
            const data = action.payload;
            const thirtyDaysAgo = Math.round(new Date().getTime() / 1000) - 60 * 60 * 24 * 30;
            // @ts-ignore
            state.themes.map((theme) => {
                if (theme.themeId == data.theme) {
                    theme.isActive = true;
                    state.currentTheme = theme;
                } else {
                    theme.isActive = false;
                }
            })
            state.slides = [];

            // фильтруем слайды с сервера, те, что старше 30 дней - удаляем
            data.slides = data.slides.filter((backendSlide: object) => {
                if (typeof backendSlide === 'undefined') {
                    return false;
                }
                if (
                    // @ts-ignore
                    typeof backendSlide?.createdAt !== 'undefined' && backendSlide?.createdAt !== null &&
                    // @ts-ignore
                    backendSlide?.createdAt < thirtyDaysAgo
                ) {
                    return false;
                }
                return true;
            });
            // map'им слайды с сервера в объекты SlideData
            data.slides.map((backendSlide: object) => {
                // @ts-ignore
                if (backendSlide?.type == 'text') {
                    state.slides.push({
                        pid: uuidv4(),
                        previewId: uuidv4(),
                        // @ts-ignore
                        src: backendSlide?.src,
                        // @ts-ignore
                        medium_url: backendSlide?.medium_url,
                        // @ts-ignore
                        thumbnail_url: backendSlide?.thumbnail_url,
                        // @ts-ignore
                        type: backendSlide?.type,
                        status: UploadStatus.ready,
                        // @ts-ignore
                        createdAt: backendSlide?.createdAt,
                        // @ts-ignore
                        pathInfoFilename: backendSlide?.pathInfoFilename,
                        // @ts-ignore
                        text: backendSlide?.text.replace(/\\"/g, '"') // делаем unescape \" => ", чтобы не появлялись слэши при повторном сохранении проекта
                    } as SlideData)
                }
                // @ts-ignore
                if (backendSlide?.type == 'photo') {
                    state.slides.push({
                        pid: uuidv4(),
                        previewId: uuidv4(),
                        // @ts-ignore
                        src: backendSlide?.src,
                        // @ts-ignore
                        medium_url: backendSlide?.medium_url,
                        // @ts-ignore
                        thumbnail_url: backendSlide?.thumbnail_url,
                        width: 1,
                        height: 1,
                        // @ts-ignore
                        type: backendSlide?.type,
                        status: UploadStatus.ready,
                        // @ts-ignore
                        createdAt: backendSlide?.createdAt,
                        // @ts-ignore
                        pathInfoFilename: backendSlide?.pathInfoFilename
                    } as SlideData)
                }
            });
            var addSlideItemIndex = state.slides.findIndex(s => s.pid === 'add-slide-item')
            if (addSlideItemIndex === -1) {
                state.slides.push({pid: 'add-slide-item', id: 'add-slide-item', status: UploadStatus.ready});
            } else {
                state.slides.push(state.slides.splice(addSlideItemIndex, 1)[0]);
            }

            // удаляем пользовательские мелодии (локальные уже были добавлены ранее в setStateFromBackend)
            state.music = state.music.filter((musicItem: MusicData) => {
                return musicItem.type === MUSIC_TYPE_LOCAL;
            });

            // добавляем пользовательские мелодии из preview
            const jStorage = JSON.parse(data.jStorage);
            if (typeof jStorage.userMusic !== 'undefined') {
                jStorage.userMusic.map((musicItem: MusicData) => {
                    state.music.push(musicItem);
                    state.musicUploadPidOnlyOneFile = musicItem.id;
                })
            }

            // фильтруем, если мелодия загружена больше 30 дней назад - удаляем
            state.music = state.music.filter((musicItem: MusicData) => {
                if (
                    typeof musicItem.createdAt !== 'undefined' && musicItem.createdAt !== null &&
                    musicItem.createdAt < thirtyDaysAgo
                ) {
                    return false;
                }
                return true;
            });
            // чиним URL'ы у мелодий
            state.music.map((musicItem: MusicData) => {
                if (data.sound.replace(
                    'local:',
                    'https://files5-cdn.slide-life.ru/sounds/') == musicItem.url) {
                    musicItem.isActive = true;
                    state.currentMusic = musicItem;
                } else {
                    musicItem.isActive = false;
                }
            });

            /**
             * Если пользователь загрузил свою мелодию
             * а потом через 2 месяца заходит опять в редактор,
             * то у него в LocalStorage сохранена state.currentMusic = загруженная мелодия
             * но в цикле выше мы фильтруем все мелодии и если она была загружена больше месяца назад - то удаляем
             * её из state.music массива, а в state.currentMusic она остаётся и в итоге не делается preview
             * чтобы починить это мы проходит ещё раз по state.music и смотрим, есть ли там isActive мелодия
             * если нет - то выставляем state.currentMusic=state.music[0]
             */
            let hasActiveMusic = false;
            state.music.map((musicItem: MusicData) => {
                if (musicItem.isActive) {
                    hasActiveMusic = true;
                }
            });
            if (!hasActiveMusic) {
                state.currentMusic = state.music[0];
                state.music[0].isActive = true;
            }

            state.previewId = data.previewId;
            state.previewData = null;

            state.fullVideoPrice = data.fullVideoPrice;
            state.fullVideoMakeTime = data.fullVideoMakeTime;
            state.fullVideoMakeTimeStr = data.fullVideoMakeTimeStr;

            state.savedProjectPaymentDone = false;
            state.savedProjectData = null;

            state.videoDuration = calculateVideoDuration(state.slides, state.currentTheme, state.themes);

            state.stateHash = sha256(getStateStringForHash(state));

            state.isPreviewRestored = true;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setCurrentTab: (state, action) => {
            state.currentTab = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setInfoC: (state, action) => {
            state.infoC = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setIsAllPhotosUploaded: (state, action) => {
            state.isAllPhotosUploaded = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        checkIsAllPhotosUploaded: (state) => {
            let isAllPhotosUploaded = true;
            state.slides.map((slide: SlideData) => {
                if (slide.type === 'text' || slide.type === 'photo') {
                    if (slide.status === UploadStatus.uploading) {
                        isAllPhotosUploaded = false;
                    }
                }
            })
            state.isAllPhotosUploaded = isAllPhotosUploaded;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setIsAllMusicUploaded: (state, action) => {
            state.isAllMusicUploaded = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        checkIsAllMusicUploaded: (state) => {
            let isAllMusicUploaded = true;
            state.music.map((music: MusicData) => {
                if (music.type === MUSIC_TYPE_USER) {
                    if (music.status === UploadStatus.uploading) {
                        isAllMusicUploaded = false;
                    }
                }
            })
            state.isAllMusicUploaded = isAllMusicUploaded;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setOnBoardingStep: (state, action) => {
            state.onBoardingStep = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        addOnBoardingShownStep: (state, action) => {
            if (!state.onBoardingStepsShown.includes(action.payload)) {
                state.onBoardingStepsShown.push(action.payload);
            }

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        resetOnBoardingShownSteps: (state) => {
            state.onBoardingStepsShown = [];
            state.onBoardingResetTime = new Date().getTime();

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setOnBoardingShow: (state, action) => {
            state.onBoardingShow = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setOnBoardingShowReorderPopup: (state, action) => {
            state.onBoardingShowReorderPopup = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setIsAddSlidePopupOpen: (state, action) => {
            state.isAddSlidePopupOpen = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setVkLaunchParams: (state, action) => {
            state.vkLaunchParams = action.payload as VkLaunchParams;

            if (state.vkLaunchParams?.vk_user_id) {
                state.userId = state.vkLaunchParams?.vk_user_id?.toString();
                // @ts-ignore
                Sentry.setUser({id: state.vkLaunchParams?.vk_user_id})
            }
            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setVkClientVersion: (state, action) => {
            state.vkClientVersion = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setVkUserProjects: (state, action) => {
            if (typeof action.payload !== 'undefined' && action.payload !== null) {
                state.vkUserProjects = action.payload;
            }
        },
        setSavedProjectPaymentDone: (state, action) => {
            state.savedProjectPaymentDone = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setAllowCheckPaymentDone: (state, action) => {
            state.allowCheckPaymentDone = action.payload;

            Sentry.setExtra('makerData', JSON.parse(JSON.stringify(state)));
        },
        setYmClientId: (state, action) => {
            state.ymClientId = action.payload;
        },
    },
})

const getStateStringForHash = (state: MakerDataState): string => {

    const activeThemeIndex = state.themes.findIndex(s => s.isActive === true);
    let activeTheme = state.currentTheme; // ЭТО НЕ ТА ТЕМА, которую выбрали, а та, которая сейчас показывается в Popup'е выбора темы
    if (activeThemeIndex >= 0) {
        activeTheme = state.themes[activeThemeIndex]; // а это как раз та тема, на которую нажали "Выбрать эту тему"
    }

    state.slidesNumReady = state.slides.filter((item) => {
        return typeof item !== 'undefined' && item != null && (item.status === UploadStatus.ready || item.status === UploadStatus.uploadSuccess) && item.pid != 'add-slide-item';
    }).length;

    let result = '';
    result += activeTheme.themeId;
    result += state.currentMusic?.url;
    state.slides.map((slide: SlideData) => {
        if (
            typeof slide !== 'undefined' &&
            (slide.type === 'text' || slide.type === 'photo')
        ) {
            result += slide.src;
            result += slide.createdAt;
            result += slide.status;
        }
    });
    return result;
}

const calculateVideoDuration = (slides: SlideData[], currentTheme: ThemeData, themes: ThemeData[]): VideoDuration | null => {

    const activeThemeIndex = themes.findIndex(s => s.isActive === true);
    let activeTheme = currentTheme; // ЭТО НЕ ТА ТЕМА, которую выбрали, а та, которая сейчас показывается в Popup'е выбора темы
    if (activeThemeIndex >= 0) {
        activeTheme = themes[activeThemeIndex]; // а это как раз та тема, на которую нажали "Выбрать эту тему"
    }

    const slidesNum = slides.filter((item) => {
        return typeof item != 'undefined' && item != null && item.status != UploadStatus.uploadError && item.pid != 'add-slide-item';
    }).length;

    if (slidesNum == 0) {
        return null;
    }

    const videoDurationSeconds = Math.floor(
        activeTheme.duration.sceneIntro +
        activeTheme.duration.scene * slidesNum +
        activeTheme.duration.sceneOutro);
    // const videoDurationSeconds = Math.floor(videoDurationFrames / 30);

    const hours = Math.floor(videoDurationSeconds / 3600);
    const minutes = Math.floor((videoDurationSeconds - (hours * 3600)) / 60);
    const seconds = videoDurationSeconds - (hours * 3600) - (minutes * 60);

    return {
        minutes: minutes,
        seconds: seconds
    } as VideoDuration;
}

export const {
    setSlideUploadProgress,
    setSlideUploadStatus,
    addSlide,
    removeSlide,
    removeAllSlides,
    setSlides,
    setCurrentSlide,
    setCurrentTheme,
    setThemeActive,
    setThemes,
    setLoaded,
    setMusic,
    setCurrentMusic,
    setIsMusicPlaying,
    addMusic,
    setMusicUploadProgress,
    removeMusic,
    setEmail,
    setStateFromBackend,
    setProjectSaveResultFromBackend,
    setPreviewSaveResultFromBackend,
    setPreviewDataFromBackend,
    setPreviewId,
    setPreviewVolume,
    setThemePopupVolume,
    setMusicVolume,
    setIsPreviewPlaying,
    setPromoCodeFromBackend,
    setPromoCodeDiscount,
    resetProjectSave,
    restorePreviewDataFromBackend,
    setCurrentTab,
    setInfoC,
    setIsAllPhotosUploaded,
    checkIsAllPhotosUploaded,
    setIsAllMusicUploaded,
    checkIsAllMusicUploaded,
    setOnBoardingStep,
    setOnBoardingShow,
    setOnBoardingShowReorderPopup,
    addOnBoardingShownStep,
    resetOnBoardingShownSteps,
    setIsAddSlidePopupOpen,
    setVkLaunchParams,
    setVkClientVersion,
    setVkUserProjects,
    setSavedProjectPaymentDone,
    setAllowCheckPaymentDone,
    setYmClientId
} = makerDataSlice.actions

export default makerDataSlice.reducer
